import type { SfxName } from '../content/types';
import type { ScratchTicket } from '../state/rules';
import { clear, h, layer } from './dom';
import { drawIcon } from '../icons/canvas';
import { setRich } from '../icons/dom';
import { iconOf } from '../icons/rich';

const W = 360;
const H = 240;
const COLS = 3;
const ROWS = 2;

/** A real scratch-off: drag across the silver coating to reveal six symbols. */
export class ScratchOff {
  readonly el: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private open = false;
  private ticket: ScratchTicket | null = null;
  private onDone: ((prize: number) => void) | null = null;
  private resolved = false;

  constructor(
    parent: HTMLElement,
    private readonly hooks: { sfx(n: SfxName): void; onOpen(): void; onClose(): void },
  ) {
    this.el = layer();
    this.panel = h('div', {
      class: 'ticket',
      attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Scratch-off ticket' },
    });
    this.el.append(this.panel);
    parent.append(this.el);
    window.addEventListener(
      'keydown',
      (e) => {
        if (!this.open) return;
        if (e.key === 'Escape' || e.key === 'Enter' || e.code === 'KeyE' || e.code === 'Space') {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (this.resolved) this.close();
          else this.revealAll();
        } else e.stopImmediatePropagation();
      },
      true,
    );
  }

  get isOpen(): boolean {
    return this.open;
  }

  private cover!: HTMLCanvasElement;
  private result!: HTMLDivElement;
  private actions!: HTMLDivElement;

  show(ticket: ScratchTicket, doubled: boolean, onDone: (prize: number) => void): void {
    this.ticket = ticket;
    this.onDone = onDone;
    this.resolved = false;
    if (!this.open) this.hooks.onOpen();
    this.open = true;
    this.el.hidden = false;
    clear(this.panel);

    const base = h('canvas', { attrs: { width: String(W), height: String(H) } });
    this.cover = h('canvas', {
      attrs: { width: String(W), height: String(H), 'aria-label': 'Scratch here' },
    });
    this.cover.style.position = 'absolute';
    this.cover.style.inset = '0';
    const stack = h('div', { class: 'ticket-stack' }, base, this.cover);
    this.result = h('div', { class: 'result', text: 'Match 3 to win!' });
    this.actions = h('div', { class: 'row' });
    this.actions.append(
      h('button', { class: 'tape', text: ':coin: Scratch it all', on: { click: () => this.revealAll() } }),
    );
    const game = ticket.game;
    const top = Math.max(...Object.values(game.prizes)) * (doubled ? 2 : 1);
    this.panel.append(
      h('h2', { text: game.name }),
      h('p', {
        text: doubled
          ? `:slots: DOUBLE PRIZE DAY! Every win pays 2x! Top prize $${top.toLocaleString()}!`
          : `Match 3 symbols. Top prize $${top.toLocaleString()}!`,
      }),
      stack,
      this.result,
      this.actions,
    );

    this.drawBase(base, ticket);
    this.drawCover();
    this.bindScratch();
  }

  private drawBase(c: HTMLCanvasElement, t: ScratchTicket): void {
    const ctx = c.getContext('2d') as CanvasRenderingContext2D;
    ctx.fillStyle = t.game.bg;
    ctx.fillRect(0, 0, W, H);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    t.cells.forEach((sym, i) => {
      const cx = (i % COLS) * (W / COLS) + W / COLS / 2;
      const cy = Math.floor(i / COLS) * (H / ROWS) + H / ROWS / 2;
      const icon = iconOf(sym);
      if (icon) drawIcon(ctx, icon, cx - 28, cy - 40, 56);
      ctx.font = '16px Anton, Impact, sans-serif';
      ctx.fillStyle = t.game.ink;
      ctx.fillText(`$${t.game.prizes[sym].toLocaleString()}`, cx, cy + 36);
      ctx.fillStyle = '#000';
    });
    ctx.strokeStyle = 'rgba(0,0,0,0.15)';
    for (let i = 1; i < COLS; i++) ctx.strokeRect((i * W) / COLS, 0, 0, H);
    ctx.strokeRect(0, H / 2, W, 0);
  }

  private drawCover(): void {
    const ctx = this.cover.getContext('2d') as CanvasRenderingContext2D;
    ctx.globalCompositeOperation = 'source-over';
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, '#c8ccd1');
    g.addColorStop(0.5, '#eef0f2');
    g.addColorStop(1, '#a9aeb4');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    ctx.font = '20px Bangers, Impact, sans-serif';
    ctx.textAlign = 'center';
    for (let i = 0; i < 6; i++) {
      const cx = (i % COLS) * (W / COLS) + W / COLS / 2;
      const cy = Math.floor(i / COLS) * (H / ROWS) + H / ROWS / 2;
      ctx.fillText('SCRATCH', cx, cy);
    }
  }

  private bindScratch(): void {
    const c = this.cover;
    let down = false;
    let lastSound = 0;
    const scratch = (e: PointerEvent): void => {
      if (!down || this.resolved) return;
      const r = c.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width) * W;
      const y = ((e.clientY - r.top) / r.height) * H;
      const ctx = c.getContext('2d') as CanvasRenderingContext2D;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = '#000'; // must be opaque, or each stroke only removes part of the coating
      ctx.beginPath();
      ctx.arc(x, y, 22, 0, Math.PI * 2);
      ctx.fill();
      const now = performance.now();
      if (now - lastSound > 70) {
        lastSound = now;
        this.hooks.sfx('scratch');
      }
      if (this.coverage() > 0.6) this.revealAll();
    };
    c.onpointerdown = (e) => {
      down = true;
      try {
        c.setPointerCapture(e.pointerId);
      } catch {
        // Capture is a nicety; scratching still works without it.
      }
      scratch(e);
    };
    c.onpointermove = scratch;
    c.onpointerup = () => (down = false);
  }

  private coverage(): number {
    const ctx = this.cover.getContext('2d') as CanvasRenderingContext2D;
    const data = ctx.getImageData(0, 0, W, H).data;
    let clearPx = 0;
    let total = 0;
    for (let i = 3; i < data.length; i += 64) {
      total++;
      if ((data[i] as number) < 40) clearPx++;
    }
    return clearPx / total;
  }

  private revealAll(): void {
    if (this.resolved || !this.ticket) return;
    this.resolved = true;
    const ctx = this.cover.getContext('2d') as CanvasRenderingContext2D;
    ctx.clearRect(0, 0, W, H);
    const t = this.ticket;
    if (t.prize > 0) {
      setRich(
        this.result,
        `:party: WINNER! ${t.winner}${t.winner}${t.winner} = $${t.prize.toLocaleString()}!`,
      );
      this.hooks.sfx(t.prize >= 100 ? 'airhorn' : 'cash');
    } else {
      setRich(this.result, `:meh: Not a winner. ${t.game.name} thanks you for your donation.`);
      this.hooks.sfx('fail');
    }
    clear(this.actions);
    this.actions.append(
      h('button', {
        class: 'tape primary',
        text: t.prize > 0 ? `Cash it ($${t.prize})` : 'Toss it',
        on: { click: () => this.close() },
      }),
    );
    (this.actions.firstElementChild as HTMLButtonElement).focus();
  }

  close(): void {
    if (!this.open) return;
    if (!this.resolved) this.revealAll();
    this.open = false;
    this.el.hidden = true;
    const prize = this.ticket?.prize ?? 0;
    const cb = this.onDone;
    this.onDone = null;
    this.hooks.onClose();
    cb?.(prize);
  }
}
